import DocumentPageShell from '@/components/sections/shared/DocumentPageShell';
import type { PolicyPageData } from '@/services/policy-page.service';
import { formatDate } from '@/utils/formatDate';

interface PolicyPageContentProps {
  locale: string;
  data: PolicyPageData;
  effectiveDateLabel?: string;
}

function toHtmlId(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(?:^-)|(?:-$)/g, '');
}

export default function PolicyPageContent({
  locale,
  data,
  effectiveDateLabel = 'Effective Date',
}: PolicyPageContentProps) {
  const contact = data.contactOptional?.[0];
  const contactHeadingId = `${toHtmlId(data.pageSlug) || 'policy'}-contact-heading`;

  return (
    <DocumentPageShell title={data.pageTitle}>
      <div className="overflow-hidden rounded-2xl border border-[#e4e4e4] bg-white p-6 shadow-sm sm:p-8 md:p-10">
        {data.datePublished ? (
          <div className="mb-6 border-b border-[#f0f0f0] pb-6 sm:mb-8">
            <p className="font-figtree text-[10px] font-bold uppercase tracking-[0.2em] text-[#A09A93]">
              {effectiveDateLabel}
            </p>
            <p className="mt-1 text-base font-medium text-[#1C1917] sm:text-lg">
              {formatDate(data.datePublished, locale)}
            </p>
          </div>
        ) : null}
        <article
          className="prose prose-base max-w-none prose-headings:mt-8 prose-headings:font-semibold prose-headings:text-[#1C1917] prose-h2:text-xl prose-h2:sm:text-2xl prose-p:text-[#404040] prose-li:text-[#404040] prose-a:font-medium prose-a:text-[#ed1c25] prose-a:no-underline hover:prose-a:underline"
          dangerouslySetInnerHTML={{ __html: data.pageContent }}
        />
      </div>
      {contact ? (
        <section
          className="mt-6 rounded-2xl bg-[#ed1c22] p-6 text-white sm:mt-8 md:p-8"
          aria-labelledby={contactHeadingId}
        >
          <h2 id={contactHeadingId} className="text-2xl font-medium">
            {contact.heading}
          </h2>
          <div
            className="prose prose-base prose-invert mt-3 max-w-none"
            dangerouslySetInnerHTML={{ __html: contact.description }}
          />
        </section>
      ) : null}
    </DocumentPageShell>
  );
}
